/**
 * Forum Overview Block — Editor
 *
 * Provides inspector controls for configuring the Forum Overview block
 * including layout, visibility toggles, custom labels, and styling options.
 *
 * @package BBP_Core
 */
import { __ } from '@wordpress/i18n';
import {
	useBlockProps,
	InspectorControls,
} from '@wordpress/block-editor';
import {
	PanelBody,
	SelectControl,
	TextControl,
	ToggleControl,
	RangeControl,
	ColorPalette,
	Placeholder,
} from '@wordpress/components';
import ServerSideRender from '@wordpress/server-side-render';
import './editor.scss';

/**
 * Skeleton loader component displayed while the server-side render loads.
 *
 * @return {JSX.Element} Skeleton loader markup.
 */
const SkeletonLoader = () => (
	<div className="bbpc-stats-skeleton">
		{ [ 1, 2, 3, 4 ].map( ( i ) => (
			<div key={ i } className="bbpc-stats-skeleton__card">
				<div className="bbpc-stats-skeleton__icon" />
				<div className="bbpc-stats-skeleton__count" />
				<div className="bbpc-stats-skeleton__label" />
			</div>
		) ) }
	</div>
);

/**
 * Edit component for the Forum Stats block.
 *
 * @param {Object}   props               Block props.
 * @param {Object}   props.attributes    Block attributes.
 * @param {Function} props.setAttributes Attribute setter.
 * @return {JSX.Element} Block editor output.
 */
export default function Edit( { attributes, setAttributes } ) {
	const blockProps = useBlockProps();

	/* Check if Forumax Pro is active. */
	const isProActive =
		window.bbpc_upsell_config?.is_pro_active === '1' ||
		window.bbpc_upsell_config?.is_pro_active === true;

	// Entire block is Pro-only — show a static mockup with overlay for free users.
	if ( ! isProActive ) {
		const mockStats = [
			{
				icon: '📋',
				count: '12',
				label: __( 'Forums', 'bbp-core' ),
			},
			{
				icon: '💬',
				count: '148',
				label: __( 'Topics', 'bbp-core' ),
			},
			{
				icon: '↩️',
				count: '1,024',
				label: __( 'Replies', 'bbp-core' ),
			},
			{
				icon: '👥',
				count: '356',
				label: __( 'Members', 'bbp-core' ),
			},
		];

		return (
			<div { ...blockProps }>
				<div className="bbpc-pro-block-overlay-wrap">
					<div className="bbpc-pro-block-overlay">
						<span className="bbpc-pro-block-overlay__badge">
							PRO
						</span>
						<p className="bbpc-pro-block-overlay__text">
							{ __(
								'This block requires Forumax Pro',
								'bbp-core'
							) }
						</p>
						<a
							href={
								window.bbpc_upsell_config?.upgrade_url ||
								'/wp-admin/admin.php?page=forumax-pricing'
							}
							target="_blank"
							rel="noopener noreferrer"
							className="bbpc-pro-block-overlay__btn"
						>
							{ __( 'Upgrade to Pro', 'bbp-core' ) }
						</a>
					</div>
					<div className="bbpc-pro-block-preview">
						<div className="bbpc-pro-mock-stats">
							{ mockStats.map( ( stat, idx ) => (
								<div
									key={ idx }
									className="bbpc-pro-mock-stats__card"
								>
									<span className="bbpc-pro-mock-stats__icon">
										{ stat.icon }
									</span>
									<span className="bbpc-pro-mock-stats__count">
										{ stat.count }
									</span>
									<span className="bbpc-pro-mock-stats__label">
										{ stat.label }
									</span>
								</div>
							) ) }
						</div>
					</div>
				</div>
			</div>
		);
	}
	const {
		layout,
		columns,
		show_forums,
		show_topics,
		show_replies,
		show_members,
		show_active_members,
		show_icons,
		enable_animation,
		label_forums,
		label_topics,
		label_replies,
		label_members,
		label_active_members,
		count_color,
		label_color,
		icon_color,
		card_bg_color,
		card_border_color,
		card_hover_bg_color,
		count_font_size,
		label_font_size,
		icon_size,
		card_gap,
		card_padding,
		card_border_radius,
		mobile_columns,
	} = attributes;

	const defaultColors = [
		{ name: __( 'Blue', 'bbp-core' ), color: '#3b82f6' },
		{ name: __( 'Green', 'bbp-core' ), color: '#10b981' },
		{ name: __( 'Red', 'bbp-core' ), color: '#ef4444' },
		{ name: __( 'Purple', 'bbp-core' ), color: '#8b5cf6' },
		{ name: __( 'Orange', 'bbp-core' ), color: '#f59e0b' },
		{ name: __( 'Indigo', 'bbp-core' ), color: '#6366f1' },
		{ name: __( 'Gray', 'bbp-core' ), color: '#6b7280' },
		{ name: __( 'White', 'bbp-core' ), color: '#ffffff' },
		{ name: __( 'Black', 'bbp-core' ), color: '#000000' },
	];

	return (
		<div { ...blockProps }>
			{ /* ======== SETTINGS TAB ======== */ }
			<InspectorControls>
				{ /* -------- Layout -------- */ }
				<PanelBody title={ __( 'Layout', 'bbp-core' ) }>
					<SelectControl
						label={ __( 'Layout Style', 'bbp-core' ) }
						value={ layout }
						options={ [
							{ label: __( 'Grid', 'bbp-core' ), value: 'grid' },
							{ label: __( 'Inline Row', 'bbp-core' ), value: 'inline' },
						] }
						onChange={ ( value ) =>
							setAttributes( { layout: value } )
						}
					/>
					{ layout === 'grid' && (
						<RangeControl
							label={ __( 'Columns', 'bbp-core' ) }
							value={ columns }
							onChange={ ( value ) =>
								setAttributes( { columns: value } )
							}
							min={ 1 }
							max={ 6 }
						/>
					) }
				</PanelBody>

				{ /* -------- Visibility -------- */ }
				<PanelBody
					title={ __( 'Visibility', 'bbp-core' ) }
					initialOpen={ false }
				>
					<ToggleControl
						label={ __( 'Show Forums Count', 'bbp-core' ) }
						checked={ show_forums }
						onChange={ ( value ) =>
							setAttributes( { show_forums: value } )
						}
					/>
					<ToggleControl
						label={ __( 'Show Topics Count', 'bbp-core' ) }
						checked={ show_topics }
						onChange={ ( value ) =>
							setAttributes( { show_topics: value } )
						}
					/>
					<ToggleControl
						label={ __( 'Show Replies Count', 'bbp-core' ) }
						checked={ show_replies }
						onChange={ ( value ) =>
							setAttributes( { show_replies: value } )
						}
					/>
					<ToggleControl
						label={ __( 'Show Members Count', 'bbp-core' ) }
						checked={ show_members }
						onChange={ ( value ) =>
							setAttributes( { show_members: value } )
						}
					/>
					<ToggleControl
						label={ __( 'Show Active Members', 'bbp-core' ) }
						checked={ show_active_members }
						onChange={ ( value ) =>
							setAttributes( { show_active_members: value } )
						}
					/>
					<ToggleControl
						label={ __( 'Show Icons', 'bbp-core' ) }
						checked={ show_icons }
						onChange={ ( value ) =>
							setAttributes( { show_icons: value } )
						}
					/>
					<ToggleControl
						label={ __( 'Enable Counter Animation', 'bbp-core' ) }
						checked={ enable_animation }
						onChange={ ( value ) =>
							setAttributes( { enable_animation: value } )
						}
					/>
				</PanelBody>

				{ /* -------- Labels -------- */ }
				<PanelBody
					title={ __( 'Custom Labels', 'bbp-core' ) }
					initialOpen={ false }
				>
					<TextControl
						label={ __( 'Forums Label', 'bbp-core' ) }
						value={ label_forums }
						onChange={ ( value ) =>
							setAttributes( { label_forums: value } )
						}
					/>
					<TextControl
						label={ __( 'Topics Label', 'bbp-core' ) }
						value={ label_topics }
						onChange={ ( value ) =>
							setAttributes( { label_topics: value } )
						}
					/>
					<TextControl
						label={ __( 'Replies Label', 'bbp-core' ) }
						value={ label_replies }
						onChange={ ( value ) =>
							setAttributes( { label_replies: value } )
						}
					/>
					<TextControl
						label={ __( 'Members Label', 'bbp-core' ) }
						value={ label_members }
						onChange={ ( value ) =>
							setAttributes( { label_members: value } )
						}
					/>
					<TextControl
						label={ __( 'Active Members Label', 'bbp-core' ) }
						value={ label_active_members }
						onChange={ ( value ) =>
							setAttributes( { label_active_members: value } )
						}
					/>
				</PanelBody>

				{ /* -------- Responsive -------- */ }
				<PanelBody
					title={ __( 'Responsive', 'bbp-core' ) }
					initialOpen={ false }
				>
					<p className="components-base-control__help">
						{ __(
							'Controls for mobile devices (< 768px).',
							'bbp-core'
						) }
					</p>
					<RangeControl
						label={ __( 'Mobile Columns', 'bbp-core' ) }
						value={ mobile_columns }
						onChange={ ( value ) =>
							setAttributes( { mobile_columns: value } )
						}
						min={ 1 }
						max={ 4 }
					/>
				</PanelBody>
			</InspectorControls>

			{ /* ======== STYLES TAB ======== */ }
			<InspectorControls group="styles">
				{ /* -------- Colors -------- */ }
				<PanelBody
					title={ __( 'Colors', 'bbp-core' ) }
					initialOpen={ false }
				>
					<p className="bbpc-color-label">
						{ __( 'Count Color', 'bbp-core' ) }
					</p>
					<ColorPalette
						colors={ defaultColors }
						value={ count_color }
						onChange={ ( value ) =>
							setAttributes( { count_color: value } )
						}
					/>

					<p className="bbpc-color-label">
						{ __( 'Label Color', 'bbp-core' ) }
					</p>
					<ColorPalette
						colors={ defaultColors }
						value={ label_color }
						onChange={ ( value ) =>
							setAttributes( { label_color: value } )
						}
					/>

					<p className="bbpc-color-label">
						{ __( 'Icon Color', 'bbp-core' ) }
					</p>
					<ColorPalette
						colors={ defaultColors }
						value={ icon_color }
						onChange={ ( value ) =>
							setAttributes( { icon_color: value } )
						}
					/>

					<p className="bbpc-color-label">
						{ __( 'Card Background', 'bbp-core' ) }
					</p>
					<ColorPalette
						colors={ defaultColors }
						value={ card_bg_color }
						onChange={ ( value ) =>
							setAttributes( { card_bg_color: value } )
						}
					/>

					<p className="bbpc-color-label">
						{ __( 'Card Border Color', 'bbp-core' ) }
					</p>
					<ColorPalette
						colors={ defaultColors }
						value={ card_border_color }
						onChange={ ( value ) =>
							setAttributes( { card_border_color: value } )
						}
					/>

					<p className="bbpc-color-label">
						{ __( 'Card Hover Background', 'bbp-core' ) }
					</p>
					<ColorPalette
						colors={ defaultColors }
						value={ card_hover_bg_color }
						onChange={ ( value ) =>
							setAttributes( { card_hover_bg_color: value } )
						}
					/>
				</PanelBody>

				{ /* -------- Typography -------- */ }
				<PanelBody
					title={ __( 'Typography', 'bbp-core' ) }
					initialOpen={ false }
				>
					<RangeControl
						label={ __( 'Count Font Size (px)', 'bbp-core' ) }
						value={ count_font_size }
						onChange={ ( value ) =>
							setAttributes( { count_font_size: value } )
						}
						min={ 0 }
						max={ 72 }
					/>
					<RangeControl
						label={ __( 'Label Font Size (px)', 'bbp-core' ) }
						value={ label_font_size }
						onChange={ ( value ) =>
							setAttributes( { label_font_size: value } )
						}
						min={ 0 }
						max={ 32 }
					/>
					<RangeControl
						label={ __( 'Icon Size (px)', 'bbp-core' ) }
						value={ icon_size }
						onChange={ ( value ) =>
							setAttributes( { icon_size: value } )
						}
						min={ 0 }
						max={ 80 }
					/>
				</PanelBody>

				{ /* -------- Spacing -------- */ }
				<PanelBody
					title={ __( 'Spacing', 'bbp-core' ) }
					initialOpen={ false }
				>
					<RangeControl
						label={ __( 'Card Gap (px)', 'bbp-core' ) }
						value={ card_gap }
						onChange={ ( value ) =>
							setAttributes( { card_gap: value } )
						}
						min={ 0 }
						max={ 60 }
					/>
					<RangeControl
						label={ __( 'Card Padding (px)', 'bbp-core' ) }
						value={ card_padding }
						onChange={ ( value ) =>
							setAttributes( { card_padding: value } )
						}
						min={ 0 }
						max={ 60 }
					/>
					<RangeControl
						label={ __(
							'Card Border Radius (px)',
							'bbp-core'
						) }
						value={ card_border_radius }
						onChange={ ( value ) =>
							setAttributes( {
								card_border_radius: value,
							} )
						}
						min={ 0 }
						max={ 30 }
					/>
				</PanelBody>
			</InspectorControls>

			{ /* ======== BLOCK PREVIEW ======== */ }
			<ServerSideRender
				block="bbp-core/forum-overview"
				attributes={ attributes }
				LoadingResponsePlaceholder={ SkeletonLoader }
				ErrorResponsePlaceholder={ () => (
					<Placeholder
						icon="warning"
						label={ __( 'BBPC Forum Overview', 'bbp-core' ) }
					>
						{ __( 'Error loading block preview.', 'bbp-core' ) }
					</Placeholder>
				) }
			/>
		</div>
	);
}
